/**
 * ConstructionBubble — floating bottom-right reminder that the zone is new.
 * Dismissible (localStorage `sim360_bubble_dismissed_v1`). Reappears after 30 days.
 */
import React, { useState } from 'react';
import { HardHat, X } from 'lucide-react';
import { detectLang } from '../utils/langState';

const KEY = 'sim360_bubble_dismissed_v1';
const TTL_DAYS = 30;

const COPY = {
  es: {
    title: 'Zona en construcción',
    body: 'Esta área del Club es nueva y sigue puliéndose. Algunas funciones pueden evolucionar pronto.',
  },
  en: {
    title: 'Under construction',
    body: 'This Club zone is brand-new and still polishing. Some features may evolve soon.',
  },
  fr: {
    title: 'Zone en construction',
    body: "Cet espace du Club est tout nouveau et continue d'évoluer. Certaines fonctions changeront prochainement.",
  },
};

const shouldShow = () => {
  try {
    const v = localStorage.getItem(KEY);
    if (!v) return true;
    const ts = parseInt(v, 10);
    if (!Number.isFinite(ts)) return true;
    if (Date.now() - ts > TTL_DAYS * 24 * 3600 * 1000) {
      localStorage.removeItem(KEY);
      return true;
    }
    return false;
  } catch (_) { return true; }
};

const ConstructionBubble = () => {
  const [visible, setVisible] = useState(() => shouldShow());
  const [lang] = useState(() => detectLang());

  const dismiss = () => {
    try { localStorage.setItem(KEY, String(Date.now())); } catch (_) { /* localStorage blocked */ }
    setVisible(false);
  };

  if (!visible) return null;
  const c = COPY[lang] || COPY.es;

  return (
    <div
      className="fixed bottom-4 right-4 z-[90] max-w-[300px] rounded-xl px-3.5 py-3 flex items-start gap-2.5 shadow-2xl"
      style={{
        background: 'linear-gradient(140deg, rgba(255,140,0,0.12) 0%, rgba(4,8,22,0.95) 70%)',
        border: '1px solid rgba(255,140,0,0.45)',
        backdropFilter: 'blur(16px)',
        boxShadow: '0 18px 48px -16px rgba(255,140,0,0.5)',
        animation: 'sim360-bubble-in 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)',
      }}
      data-testid="construction-bubble"
    >
      <div className="shrink-0 mt-0.5" style={{ animation: 'sim360-helmet-wobble 2.4s ease-in-out infinite' }}>
        <HardHat className="w-5 h-5 text-[#ff8c00]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#ff8c00] font-bold mb-0.5">
          {c.title}
        </div>
        <div className="text-[11px] text-[#cfd9e3] leading-snug">{c.body}</div>
      </div>
      <button
        onClick={dismiss}
        data-testid="construction-bubble-dismiss"
        aria-label="Dismiss"
        className="text-[#9EB4C0] hover:text-white transition shrink-0"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <style>{`
        @keyframes sim360-bubble-in {
          from { opacity: 0; transform: translateY(30px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes sim360-helmet-wobble {
          0%, 65%, 100% { transform: rotate(0deg); }
          70% { transform: rotate(-8deg); }
          78% { transform: rotate(8deg); }
          86% { transform: rotate(-4deg); }
          94% { transform: rotate(0deg); }
        }
      `}</style>
    </div>
  );
};

export default ConstructionBubble;
